import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Card,
  NumberInput,
  Page,
  SimpleSelect,
  SwitchInput,
  TextInput,
  UrlInput,
  FloatingSave,
} from 'corso-admin-ui';
import {
  CrewClaimResolutionMethodEnum,
  isTruthy,
  maxPolicyEligibilityDays,
} from 'corso-types';
import { FormEventHandler, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import GiftCardTypeSelect from '~/features/settings/components/GiftCardTypeSelect';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
} from '~/hooks/useConfigSettings';
import { warrantySettingsFormSchema } from '~/types';
import { getNullableNumberValue, handleLifetimeFieldValue } from '~/utils/form';

export default function WarrantiesSettingsPage() {
  const formId = 'warranty-settings';
  const formRef = useRef<HTMLFormElement>(null);

  const { data } = useConfigSettings(({ warranties }) => warranties);
  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    register,
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(warrantySettingsFormSchema),
    values: data,
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => saveChanges({ warranties: values }))(event).catch(
      console.error,
    );
  };

  const isWarrantiesEnabled = watch('isWarrantiesEnabled');

  const orderPrefix = watch('warrantyOrderPrefix');

  const defaultRequestResolutionMethodOptions = [
    {
      label: 'Warranty Review',
      value: CrewClaimResolutionMethodEnum.warrantyReview,
    },
    {
      label: 'Gift Card',
      value: CrewClaimResolutionMethodEnum.giftCard,
    },
    {
      label: 'Other - (Resolved Outside Of Corso)',
      value: CrewClaimResolutionMethodEnum.other,
    },
  ];

  return (
    <Page
      title="Warranties"
      secondaryActions={[
        isWarrantiesEnabled && {
          id: 'disable-returns',
          content: 'Turn Off',
          onAction: () => {
            setValue('isWarrantiesEnabled', false, {
              shouldDirty: true,
            });
            formRef.current?.requestSubmit();
          },
        },
      ].filter(isTruthy)}
      primaryAction={
        isWarrantiesEnabled
          ? undefined
          : {
              onAction: () => {
                setValue('isWarrantiesEnabled', true, {
                  shouldDirty: true,
                });
                formRef.current?.requestSubmit();
              },
              content: 'Turn On',
            }
      }
    >
      <form
        id={formId}
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={submitHandler}
        onReset={(e) => {
          e.preventDefault();
          reset();
        }}
      >
        {!isWarrantiesEnabled && (
          <Alert
            elevated
            variant="info"
            heading="Warranties are Disabled. Settings can be configured, but warranties will not be offered to customers."
          />
        )}

        <FloatingSave
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          form={formId}
        />
        <Card>
          {/* // ? do we want an addon or require full URLs that are HTTPS; this would better with input masking */}
          <UrlInput
            id="warranty-policy-url"
            label="Policy URL"
            placeholder="https://company.com/warranties-policy"
            {...register('warrantyPolicyUrl')}
            error={errors.warrantyPolicyUrl?.message}
          />
          <Controller
            control={control}
            name="defaultWarrantyDays"
            render={({ field, fieldState }) => (
              <NumberInput
                id="warranty-validity-days"
                label="Warranty Window"
                required
                value={handleLifetimeFieldValue(field.value)}
                onChange={(event) =>
                  field.onChange(getNullableNumberValue(event))
                }
                disabled={field.value === maxPolicyEligibilityDays}
                error={fieldState.error?.message}
                details="The number of days from the date an order was fulfilled that a customer can request a warranty review."
                addon={{
                  insideEnd:
                    field.value === maxPolicyEligibilityDays ? null : 'Days',
                  outsideStart: (
                    <div className="mr-4 flex items-center">
                      <SwitchInput
                        label="Lifetime"
                        id="lifetime-warranty"
                        checked={field.value === maxPolicyEligibilityDays}
                        onChange={(e) => {
                          if (e.target.checked) {
                            field.onChange(maxPolicyEligibilityDays);
                          } else {
                            field.onChange(365);
                          }
                        }}
                      />
                    </div>
                  ),
                }}
              />
            )}
          />

          <SwitchInput // this is just for visual reference to the user
            defaultChecked
            disabled
            id="customer-image-required"
            label="Require Media Upload"
            details="Enforce that customers provide an image or video when submitting a warranty request."
          />

          <Controller
            control={control}
            name="isWarrantyInspectionEnabled"
            defaultValue={false}
            render={({ field: { onChange, value }, fieldState }) => (
              <SwitchInput
                id="warranty-inspection-enabled"
                label="Inspections"
                details="Ability to add an inspection to a warranty request."
                checked={!!value}
                onChange={onChange}
                error={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="isMultipleWarrantyClaimsAllowed"
            defaultValue={false}
            render={({ field: { onChange, value }, fieldState }) => (
              <SwitchInput
                id="is-multiple-warranty-claims-allowed"
                label="Allow Multiple Claims"
                details="Allow customers to submit multiple warranty claims for the same line item."
                checked={!!value}
                onChange={onChange}
                error={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="defaultWarrantyRequestedResolutionMethod"
            defaultValue={CrewClaimResolutionMethodEnum.warrantyReview}
            render={({ field: { onChange, value }, fieldState }) => (
              <SimpleSelect
                label="Default Requested Resolution Method"
                details="The default resolution method to use when a Warranty claim is created."
                options={defaultRequestResolutionMethodOptions}
                value={value}
                onChange={onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </Card>

        <Card>
          <Card.Heading as="h2">E-Commerce Platform</Card.Heading>

          <Controller
            control={control}
            name="warrantyGiftCardType"
            render={({ field, fieldState }) => (
              <GiftCardTypeSelect
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={fieldState.error?.message}
              />
            )}
          />

          <TextInput
            id="warranty-order-prefix"
            label="Warranty Order Prefix"
            details={
              orderPrefix
                ? `The prefix to add to warranty replacement orders, e.g. ${orderPrefix}1234`
                : 'The prefix to add to warranty replacement orders.'
            }
            placeholder="#"
            {...register('warrantyOrderPrefix')}
            error={errors.warrantyOrderPrefix?.message}
          />
        </Card>
      </form>
    </Page>
  );
}
